import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CircleDot, Timer, Trophy, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export const CoinBalancePage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const [betAmount, setBetAmount] = useState(100);
  const [isPlaying, setIsPlaying] = useState(false);
  const [balanceTime, setBalanceTime] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [coinPosition, setCoinPosition] = useState({ x: 50, y: 0 });
  const [lemonTilt, setLemonTilt] = useState(0);
  const canvasRef = useRef(null);
  const gameLoopRef = useRef(null);
  const timerRef = useRef(null);

  const drawGame = useCallback((coinPos, tilt, time, fallen) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Background - water glass effect
    const gradient = ctx.createRadialGradient(width/2, height/2, 0, width/2, height/2, height/2);
    gradient.addColorStop(0, '#1a3a4a');
    gradient.addColorStop(1, '#0a1520');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    
    // Water effect
    ctx.fillStyle = 'rgba(0, 122, 255, 0.2)';
    ctx.beginPath();
    ctx.moveTo(0, height * 0.6);
    for (let x = 0; x <= width; x += 10) {
      const wave = Math.sin(x * 0.02 + Date.now() * 0.002) * 5;
      ctx.lineTo(x, height * 0.6 + wave);
    }
    ctx.lineTo(width, height);
    ctx.lineTo(0, height);
    ctx.fill();
    
    // Glass outline
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(width * 0.2, height * 0.3);
    ctx.lineTo(width * 0.15, height * 0.9);
    ctx.lineTo(width * 0.85, height * 0.9);
    ctx.lineTo(width * 0.8, height * 0.3);
    ctx.stroke();
    
    // Lemon
    const lemonX = width / 2;
    const lemonY = height * 0.55;
    
    ctx.save();
    ctx.translate(lemonX, lemonY);
    ctx.rotate(tilt * Math.PI / 180);
    
    // Lemon body
    ctx.fillStyle = '#FFD600';
    ctx.beginPath();
    ctx.ellipse(0, 0, 60, 40, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Lemon highlight
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.beginPath();
    ctx.ellipse(-15, -10, 20, 15, -0.3, 0, Math.PI * 2);
    ctx.fill();
    
    // Coin on lemon
    if (!fallen) {
      const coinX = (coinPos.x - 50) * 1.2;
      const coinY = -45 + coinPos.y;
      
      ctx.fillStyle = '#FFD700';
      ctx.beginPath();
      ctx.arc(coinX, coinY, 20, 0, Math.PI * 2);
      ctx.fill();
      
      // Coin shine
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.beginPath();
      ctx.arc(coinX - 5, coinY - 5, 8, 0, Math.PI * 2);
      ctx.fill();
      
      // Coin symbol
      ctx.fillStyle = '#B8860B';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('₹', coinX, coinY + 5);
    }
    
    ctx.restore();
    
    // Fallen coin splash
    if (fallen) {
      ctx.fillStyle = 'rgba(255, 215, 0, 0.6)';
      for (let i = 0; i < 10; i++) {
        const angle = (i / 10) * Math.PI * 2;
        const dist = 30 + Math.random() * 20;
        ctx.beginPath();
        ctx.arc(lemonX + Math.cos(angle) * dist, lemonY + 50 + Math.sin(angle) * dist * 0.5, 5, 0, Math.PI * 2);
        ctx.fill();
      }
      
      ctx.fillStyle = '#FF3B30';
      ctx.font = 'bold 32px Rajdhani, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('SPLASH!', width / 2, height * 0.4);
    }
    
    // Timer display
    ctx.fillStyle = '#00E701';
    ctx.font = 'bold 48px JetBrains Mono, monospace';
    ctx.textAlign = 'center';
    ctx.fillText(`${time.toFixed(1)}s`, width / 2, 60);
    
    // Multiplier
    const mult = 1 + time * 0.1;
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 24px Rajdhani, sans-serif';
    ctx.fillText(`${mult.toFixed(2)}x`, width / 2, 95);
    
  }, []);

  const startGame = async () => {
    if (betAmount <= 0 || betAmount > (user?.balance || 0)) {
      toast.error("Invalid bet amount");
      return;
    }

    setIsPlaying(true);
    setGameOver(false);
    setBalanceTime(0);
    setCoinPosition({ x: 50, y: 0 });
    setLemonTilt(0);

    let time = 0;
    let coinPos = { x: 50, y: 0 };
    let tilt = 0;
    let velocity = 0;
    
    // Physics simulation
    gameLoopRef.current = setInterval(() => {
      // Natural drift and player's (simulated) balance attempts
      const drift = Math.sin(Date.now() * 0.003) * 0.5;
      const gravity = 0.02;
      
      // Tilt affects coin position
      velocity += tilt * 0.01;
      velocity += drift;
      velocity *= 0.98; // Friction
      
      coinPos.x += velocity;
      tilt += (Math.random() - 0.5) * 2; // Random tilting
      tilt *= 0.95;
      
      // Check if coin fell
      if (Math.abs(coinPos.x - 50) > 35 || Math.abs(tilt) > 30) {
        clearInterval(gameLoopRef.current);
        clearInterval(timerRef.current);
        setGameOver(true);
        setIsPlaying(false);
        drawGame(coinPos, tilt, time, true);
        
        // Submit result
        submitResult(time);
        return;
      }
      
      setCoinPosition({ ...coinPos });
      setLemonTilt(tilt);
      drawGame(coinPos, tilt, time, false);
    }, 50);
    
    // Timer
    timerRef.current = setInterval(() => {
      time += 0.1;
      setBalanceTime(time);
    }, 100);
  };

  const submitResult = async (time) => {
    try {
      const response = await axios.post(`${API}/games/coinbalance/play`, {
        game_type: "coinbalance",
        bet_amount: betAmount,
        bet_data: { balance_time: time }
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.won) {
        toast.success(`Won ₹${response.data.winnings.toFixed(2)}! Balanced for ${time.toFixed(1)}s`);
      } else {
        toast.error(`Need 3+ seconds to win. You balanced for ${time.toFixed(1)}s`);
      }
      
      await refreshBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Game error");
    }
  };

  const stopGame = () => {
    clearInterval(gameLoopRef.current);
    clearInterval(timerRef.current);
    setIsPlaying(false);
    setGameOver(true);
    submitResult(balanceTime);
  };

  useEffect(() => {
    drawGame({ x: 50, y: 0 }, 0, 0, false);
    
    return () => {
      if (gameLoopRef.current) clearInterval(gameLoopRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [drawGame]);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-[#FFD600]/20 flex items-center justify-center">
            <CircleDot className="w-6 h-6 text-[#FFD600]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-['Rajdhani']" data-testid="coinbalance-title">Coin Balance</h1>
            <p className="text-[#A1A1AA] text-sm">Balance the coin on the floating lemon!</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Game Canvas */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl bg-[#1A1B1F] border border-white/[0.08] overflow-hidden">
              <canvas
                ref={canvasRef}
                width={600}
                height={400}
                className="w-full h-auto"
                data-testid="coinbalance-canvas"
              />
              
              {/* Controls */}
              <div className="p-6 border-t border-white/[0.08]">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <label className="text-[#A1A1AA] text-sm mb-2 block">Bet Amount (₹)</label>
                    <Input
                      type="number"
                      value={betAmount}
                      onChange={(e) => setBetAmount(Number(e.target.value))}
                      disabled={isPlaying}
                      className="h-12 bg-black/20 border-white/10 text-white font-mono text-lg"
                      data-testid="coinbalance-bet-input"
                    />
                  </div>
                  
                  <div className="flex gap-2 items-end">
                    {[50, 100, 500].map(amount => (
                      <Button
                        key={amount}
                        variant="outline"
                        size="sm"
                        onClick={() => setBetAmount(amount)}
                        disabled={isPlaying}
                        className="border-white/10 text-white/60 hover:text-white hover:bg-white/5"
                      >
                        {amount}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div className="mt-4">
                  {!isPlaying ? (
                    <Button
                      onClick={startGame}
                      disabled={betAmount <= 0 || betAmount > (user?.balance || 0)}
                      className="w-full h-14 bg-[#00E701] hover:bg-[#00C201] text-black font-bold text-lg btn-glow"
                      data-testid="coinbalance-start-btn"
                    >
                      START GAME
                    </Button>
                  ) : (
                    <Button
                      onClick={stopGame}
                      className="w-full h-14 bg-[#FF3B30] hover:bg-[#E63329] text-white font-bold text-lg"
                      data-testid="coinbalance-stop-btn"
                    >
                      STOP & COLLECT ({(1 + balanceTime * 0.1).toFixed(2)}x)
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Current Stats */}
            <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
              <div className="flex items-center gap-2 mb-4">
                <Timer className="w-5 h-5 text-[#A1A1AA]" />
                <h3 className="text-lg font-bold text-white font-['Rajdhani']">Current Round</h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-[#A1A1AA]">Time Balanced</span>
                  <span className="text-2xl font-mono font-bold text-[#00E701]">{balanceTime.toFixed(1)}s</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#A1A1AA]">Multiplier</span>
                  <span className="text-2xl font-mono font-bold text-white">{(1 + balanceTime * 0.1).toFixed(2)}x</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#A1A1AA]">Potential Win</span>
                  <span className="text-2xl font-mono font-bold text-[#FFD600]">
                    ₹{(betAmount * (1 + balanceTime * 0.1)).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* How to Play */}
            <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
              <div className="flex items-center gap-2 mb-4">
                <HelpCircle className="w-5 h-5 text-[#A1A1AA]" />
                <h3 className="text-lg font-bold text-white font-['Rajdhani']">How to Play</h3>
              </div>
              
              <ul className="space-y-3 text-[#A1A1AA] text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFD600]">1.</span>
                  The coin must balance on the lemon floating in water
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFD600]">2.</span>
                  Longer balance = higher multiplier (10% per second)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFD600]">3.</span>
                  Balance for at least 3 seconds to win
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF3B30]">!</span>
                  If the coin falls, you lose your bet
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
